import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { ThinkingAnimation } from './ThinkingAnimation';
import { ReferenceModal } from './ReferenceModal';
import { formatPageNumbers } from '../utils/formatters';
import { Brain, ChevronLeft, ChevronRight, MessageSquare } from 'lucide-react';

export const MessageContent = ({ message, isLatest, versions }) => {
  const [showActions, setShowActions] = useState(false);
  const [selectedReference, setSelectedReference] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentVersionIndex, setCurrentVersionIndex] = useState(0);

  // 如果有多个版本，确保当前版本索引有效
  useEffect(() => {
    if (versions && versions.length > 0) {
      setCurrentVersionIndex(versions.length - 1); // 默认显示最新版本
    }
  }, [versions]);

  useEffect(() => {
    if (isLatest && message.type === 'ai' && message.content !== '' && !message.isStreaming) {
      const timer = setTimeout(() => {
        setShowActions(true);
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [message.content, message.isStreaming, isLatest]);

  const formatContent = (content) => {
    if (typeof content !== 'string') return '';
    // Replace \n with actual newlines and handle references
    return content
      .replace(/\\n/g, '\n')
      .replace(/<ref>(\[\d+\](?:\[\d+\])*)<\/ref>/g, '$1');
  };

  const formatDocName = (docName) => {
    if (!docName) return '';
    // 移除以8位数字开头的部分（20XXXXXX）
    return docName.replace(/^20\d{6}/, '').trim();
  };

  const handleReferenceClick = (reference) => {
    setSelectedReference(reference);
    setIsModalOpen(true);
  };

  // 处理版本切换
  const handlePrevVersion = () => {
    if (versions && currentVersionIndex > 0) {
      setCurrentVersionIndex(currentVersionIndex - 1);
    }
  };

  const handleNextVersion = () => {
    if (versions && currentVersionIndex < versions.length - 1) {
      setCurrentVersionIndex(currentVersionIndex + 1);
    }
  };

  // 确定当前显示的消息内容
  const displayMessage = versions && versions.length > 0
    ? versions[currentVersionIndex]
    : message;

  return (
    <div className={`rounded-xl p-4 ${message.type === 'user'
        ? 'bg-blue-600 text-white'
        : 'bg-gray-100'
      }`}>
      <div className="prose max-w-none">
        {message.type === 'ai' && message.content === '' ? (
          <ThinkingAnimation />
        ) : (
          <ReactMarkdown className={`leading-relaxed [&>p]:my-0 ${message.type === 'user' ? 'text-white' : 'text-gray-700'
            }`}>
            {formatContent(displayMessage.content)}
          </ReactMarkdown>
        )}
      </div>

      {message.type === 'ai' && (
        <>
          {displayMessage.references && displayMessage.references.length > 0 && !displayMessage.isStreaming && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-sm text-gray-500 mb-2">以上回答基于下列文件：</p>
              <ul className="text-sm text-gray-600 space-y-0.5 leading-relaxed">
                {[...displayMessage.references]
                  .sort((a, b) => a.index_id - b.index_id)
                  .map((ref, index) => (
                    <li
                      key={index}
                      onClick={() => handleReferenceClick(ref)}
                      className="cursor-pointer hover:text-blue-600 transition-colors duration-200"
                    >
                      [{ref.index_id}] {formatDocName(ref.doc_name)}{formatPageNumbers(ref.page_number) && ` (${formatPageNumbers(ref.page_number)})`}
                    </li>
                  ))}
              </ul>
            </div>
          )}

          {displayMessage.usage && (
            <div className="mt-2 pt-2 border-t border-gray-200 text-xs text-gray-400 flex items-center justify-between">
              <div className="flex items-center gap-1">
                <Brain className="w-3.5 h-3.5" />
                引用知识库内的相关内容消耗 {displayMessage.usage?.input_tokens || 0} 个词元，
                输出解答消耗 {displayMessage.usage?.output_tokens || 0} 个词元，
                合计消耗 {(displayMessage.usage?.input_tokens || 0) + (displayMessage.usage?.output_tokens || 0)} 个词元。
              </div>

              <div className="flex items-center">
                {/* 版本切换按钮 */}
                {versions && versions.length > 1 && (
                  <div className="flex items-center mr-2 bg-gray-200/70 rounded-full px-1">
                    <button
                      onClick={handlePrevVersion}
                      disabled={currentVersionIndex === 0}
                      className={`p-1 rounded-full ${currentVersionIndex === 0 ? 'text-gray-400 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-300'}`}
                    >
                      <ChevronLeft size={12} />
                    </button>
                    <span className="text-xs text-gray-600 mx-1">
                      {currentVersionIndex + 1}/{versions.length}
                    </span>
                    <button
                      onClick={handleNextVersion}
                      disabled={currentVersionIndex === versions.length - 1}
                      className={`p-1 rounded-full ${currentVersionIndex === versions.length - 1 ? 'text-gray-400 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-300'}`}
                    >
                      <ChevronRight size={12} />
                    </button>
                  </div>
                )}

                {/* 重新回答按钮 - 只在最后一条AI消息显示 */}
                {message.type === 'ai' && !message.isStreaming && message.onRegenerate && isLatest && (
                  <div className="flex items-center gap-1">
                    <a
                      href="https://www.kdocs.cn/l/cosFxDuyhHsa"
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-blue-500 flex items-center gap-1 py-1 px-2 rounded hover:bg-blue-50 transition-colors duration-200"
                    >
                      <MessageSquare className="w-3 h-3" />
                      我要反馈
                    </a>
                    <button
                      onClick={() => message.onRegenerate()}
                      className="text-xs text-orange-500 flex items-center gap-1 py-1 px-2 rounded hover:bg-orange-50 transition-colors duration-200"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
                        <path d="M3 3v5h5"></path>
                      </svg>
                      重新回答
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          <ReferenceModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            reference={selectedReference}
          />
        </>
      )}
    </div>
  );
}; 
